import { useCallback, useMemo } from 'react'
import useModelsStore from '../stores/models'
import usePrefStore from '../stores/prefs'
import type { Model, RawModel, GammeData } from '../types'
import { isVisibleRetailFamille } from '../libs/retail_rules'

/** Entrée de liste traitée (label résolu dans la langue courante) */
interface RetailListItem {
   id: string
   label: string
   order?: number
   [key: string]: unknown
}

type SortField = 'id' | 'label' | 'design'

const useRetailCollections = () => {
   const { retail_collections, retail_familles, portes, typologies, cycles, retail_models } = useModelsStore()
   const { currentLang: curLang, defaultLang: defLang } = usePrefStore()

   const retailGammeSort = useCallback((a: RetailListItem, b: RetailListItem) => {
      const specialRetailGammes: Record<string, string> = {
         M08: ' A',
         M09: ' B',
         M15: ' C'
      }
      let valA, valB

      valA = specialRetailGammes[a.id] ?? String(a.id)
      valB = specialRetailGammes[b.id] ?? String(b.id)
      return valA.localeCompare(valB)
   }, [])

   const sortOnLabel = useCallback((a: RetailListItem, b: RetailListItem) => {
      return a.label.localeCompare(b.label)
   }, [])

   const sortOnIdString = useCallback((a: RetailListItem, b: RetailListItem) => {
      return a.id.localeCompare(b.id)
   }, [])

   function isExcluBoutique(a_model: RawModel | null | undefined): boolean {
      if (!a_model) return false
      return !!(a_model as unknown as Record<string, unknown>).excbou
   }

   /**
    * Extrait un champ de texte dans la langue demandée
    */
   const getTextInLang = useCallback(
      (obj: Record<string, unknown>, field: string, lang: string | null): string => {
         const obj_field = obj[field]
         let text: string

         if (!obj_field) {
            text = '?1'
         } else {
            if (typeof obj_field === 'object') {
               if (!lang) {
                  text = (obj_field as Record<string, string>)[curLang] || '?2'
               } else {
                  text = (obj_field as Record<string, string>)[lang] || (obj_field as Record<string, string>)[defLang] || '?3'
               }
            } else {
               text = '?4'
            }
         }
         return text
      },
      [curLang, defLang]
   )

   const getRetailGammes = useCallback(
      (lang: string | null = null): Record<string, RetailListItem> => {
         // Si retail_collections est renseigné, on l'utilise
         if (Object.keys(retail_collections).length > 0) {
            return Object.keys(retail_collections).reduce<Record<string, RetailListItem>>((prev, id) => {
               const a_gamme = retail_collections[id] as GammeData & Record<string, unknown>
               const label = getTextInLang(a_gamme, 'label', lang)

               return {
                  ...prev,
                  [id]: { id, order: a_gamme.order, label }
               }
            }, {})
         }
         // Sinon, on dérive les gammes depuis les retail_models
         const gammeIds = new Set<string>()
         Object.values(retail_models).forEach((m) => {
            if (m.gamme) gammeIds.add(m.gamme)
         })
         return Array.from(gammeIds).reduce<Record<string, RetailListItem>>((prev, id) => {
            return { ...prev, [id]: { id, label: id } }
         }, {})
      },
      [retail_collections, retail_models, getTextInLang]
   )

   const getRetailGammesAsArray = useCallback(
      (lang: string | null = null): RetailListItem[] => {
         const obj = getRetailGammes(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getRetailGammes]
   )

   const getRetailGammesAsSortedArray = useCallback(
      (lang: string | null = null): RetailListItem[] => {
         return getRetailGammesAsArray(lang).sort(retailGammeSort)
      },
      [getRetailGammesAsArray, retailGammeSort]
   )

   const getRetailGammeById = useCallback(
      (id: string, lang: string | null = null): RetailListItem | Record<string, never> => {
         const arr = getRetailGammesAsArray(lang).filter((gamme) => gamme.id === id)
         if (arr.length === 1) {
            return { ...arr[0] }
         } else {
            console.error(`getRetailGammeById(${id}) - Pas trouvé !`)
            return {}
         }
      },
      [getRetailGammesAsArray]
   )

   const getRetailCollections = useCallback(
      (lang: string | null = null): Record<string, RetailListItem> => {
         const gammes = getRetailGammes()

         const colls = Object.keys(gammes).reduce<Record<string, unknown>>((prev, key) => {
            const gammeData = retail_collections[key] as GammeData | undefined
            if (!gammeData?.collections) return prev
            return { ...prev, ...gammeData.collections }
         }, {})

         return Object.keys(colls).reduce<Record<string, RetailListItem>>((prev, id) => {
            const a_coll = colls[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_coll, 'label', lang)

            return { ...prev, [id]: { id, label } }
         }, {})
      },
      [retail_collections, getRetailGammes, getTextInLang]
   )

   const getRetailCollectionsAsArray = useCallback(
      (lang: string | null = null): RetailListItem[] => {
         const obj = getRetailCollections(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getRetailCollections]
   )

   const getRetailCollectionsAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): RetailListItem[] => {
         let cmpFunc: (a: RetailListItem, b: RetailListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getRetailCollectionsAsArray(lang).sort(cmpFunc)
      },
      [getRetailCollectionsAsArray, sortOnIdString, sortOnLabel]
   )

   const getRetailCollectionsInGammes = useCallback(
      (gammes: string | string[], lang: string | null = null): Record<string, RetailListItem> => {
         let arr_gammes: string[]

         if (Array.isArray(gammes)) {
            arr_gammes = gammes
         } else {
            arr_gammes = [gammes]
         }

         // retail_collections n'est pas peuplé côté backend (pas dans la liste AppWrite poussée par
         // ++aw-push-lists.js) → on dérive silencieusement les "collections" depuis retail_models.
         // Sans ce fallback, getRetailGammes renvoie les gammes dérivées des modèles (M08, M09, …) que
         // retail_collections ne contient pas, ce qui spammait des `console.error` à chaque render.
         const useFallback = Object.keys(retail_collections).length === 0

         const ret = arr_gammes.reduce<Record<string, RetailListItem>>((prev, gamme) => {
            if (useFallback) {
               // Collections dérivées des modèles de la gamme demandée
               return Object.values(retail_models).reduce((acc, m) => {
                  if (m.gamme === gamme && m.collec && !acc[m.collec]) {
                     acc[m.collec] = { id: m.collec, label: m.collec }
                  }
                  return acc
               }, prev)
            }
            if (typeof retail_collections[gamme] !== 'object') {
               console.error(`getRetailCollectionsInGammes(${gamme}, ${lang}) - Pas un objet !`)
               return { ...prev }
            }
            const colls = (retail_collections[gamme] as GammeData).collections
            if (typeof colls !== 'object' || !colls) {
               console.error(`getRetailCollectionsInGammes(${gamme}, ${lang}) - collections: Pas un objet !`)
               return { ...prev }
            }
            return {
               ...prev,
               ...Object.keys(colls).reduce<Record<string, RetailListItem>>((prev, id) => {
                  const a_coll = colls[id] as unknown as Record<string, unknown>
                  const label = getTextInLang(a_coll, 'label', lang)

                  return {
                     ...prev,
                     [id]: { id, label }
                  }
               }, {})
            }
         }, {})
         return ret
      },
      [retail_collections, retail_models, getTextInLang]
   )

   const getRetailGammeForCollectionId = useCallback(
      (id: string, lang: string | null = null): RetailListItem | undefined => {
         return getRetailGammesAsArray().find((gamme) => {
            return getRetailCollectionsInGammes(gamme.id)[id] !== undefined
         })
      },
      [getRetailCollectionsInGammes, getRetailGammesAsArray]
   )

   const getRetailCollectionsInGammesAsArray = useCallback(
      (gammes: string | string[], lang: string | null = null): RetailListItem[] => {
         const obj = getRetailCollectionsInGammes(gammes, lang)
         return Object.keys(obj).map((id) => {
            return { id, ...obj[id] }
         })
      },
      [getRetailCollectionsInGammes]
   )

   const getRetailCollectionsInGammesAsSortedArray = useCallback(
      (gammes: string | string[], field: SortField = 'id', lang: string | null = null): RetailListItem[] => {
         let cmpFunc: (a: RetailListItem, b: RetailListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getRetailCollectionsInGammesAsArray(gammes, lang).sort(cmpFunc)
      },
      [getRetailCollectionsInGammesAsArray, sortOnIdString, sortOnLabel]
   )

   const getRetailCollectionById = useCallback(
      (id: string, lang: string | null = null): RetailListItem | Record<string, never> => {
         const all = getRetailCollectionsInGammesAsArray(Object.keys(getRetailGammes()))
         const arr = all.filter((collection) => collection.id === id)
         if (arr.length === 1) {
            return { ...arr[0] }
         }
         // Pas trouvé : si retail_collections est vide (jamais peuplé par le back), on retourne
         // un fallback {id, label: id} silencieusement plutôt que de spammer la console.
         if (Object.keys(retail_collections).length === 0) {
            return { id, label: id }
         }
         console.error(`getRetailCollectionById(${id}) - Pas trouvé !`)
         return {}
      },
      [getRetailCollectionsInGammesAsArray, getRetailGammes, retail_collections]
   )

   const destructModel = useCallback(
      (model: RawModel, id: string, lang: string | null = null): Model => {
         const desig = getTextInLang(model as unknown as Record<string, unknown>, 'desig', lang)
         const label = getTextInLang(model as unknown as Record<string, unknown>, 'label', lang)
         const argu = getTextInLang(model as unknown as Record<string, unknown>, 'argu', lang)

         return { ...model, id, desig, label, argu } as Model
      },
      [getTextInLang]
   )

   // Mémoisation du tableau de tous les modèles retail (recalculé uniquement quand retail_models change)
   const _allRetailModelsArray = useMemo((): Model[] => {
      const out: Model[] = []
      for (const id of Object.keys(retail_models)) {
         out.push(destructModel(retail_models[id], id, null))
      }
      return out
   }, [retail_models, destructModel])

   const getAllRetailModels = useCallback(
      (lang: string | null = null): Record<string, Model> => {
         if (!lang) {
            const out: Record<string, Model> = {}
            for (const m of _allRetailModelsArray) out[m.id] = m
            return out
         }
         const out: Record<string, Model> = {}
         for (const id of Object.keys(retail_models)) {
            out[id] = destructModel(retail_models[id], id, lang)
         }
         return out
      },
      [retail_models, destructModel, _allRetailModelsArray]
   )

   const getAllRetailModelsAsArray = useCallback(
      (lang: string | null = null): Model[] => {
         if (!lang) return _allRetailModelsArray
         return Object.keys(retail_models).map(id => destructModel(retail_models[id], id, lang))
      },
      [retail_models, destructModel, _allRetailModelsArray]
   )

   const getAllRetailModelsAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): Model[] => {
         let cmpFunc: (a: Model, b: Model) => number

         if (field === 'id') {
            cmpFunc = (a, b) => a.id.localeCompare(b.id)
         } else if (field === 'design') {
            cmpFunc = (a, b) => (a.desig ?? '').localeCompare(b.desig ?? '')
         } else {
            cmpFunc = (a, b) => a.label.localeCompare(b.label)
         }
         return [...getAllRetailModelsAsArray(lang)].sort(cmpFunc)
      },
      [getAllRetailModelsAsArray]
   )

   const getRetailModelsInCollection = useCallback(
      (collection: string, lang: string | null = null): Record<string, Model> => {
         return Object.keys(retail_models).reduce<Record<string, Model>>((prev, id) => {
            const a_model = retail_models[id]

            if (a_model.collec === collection) {
               return { ...prev, [id]: destructModel(a_model, id, lang) }
            } else {
               return prev
            }
         }, {})
      },
      [retail_models, destructModel]
   )

   const getRetailModelsInCollectionAsArray = useCallback(
      (collection: string, lang: string | null = null): Model[] => {
         const obj = getRetailModelsInCollection(collection, lang)
         return Object.keys(obj).map((id) => {
            return { ...obj[id] }
         })
      },
      [getRetailModelsInCollection]
   )

   const getRetailModelsInCollectionAsSortedArray = useCallback(
      (collection: string, field: SortField = 'id', lang: string | null = null): Model[] => {
         let cmpFunc: (a: Model, b: Model) => number

         if (field === 'id') {
            cmpFunc = (a, b) => a.id.localeCompare(b.id)
         } else {
            cmpFunc = (a, b) => a.label.localeCompare(b.label)
         }
         return getRetailModelsInCollectionAsArray(collection, lang).sort(cmpFunc)
      },
      [getRetailModelsInCollectionAsArray]
   )

   const getRetailModelById = useCallback(
      (id: string, lang: string | null = null): Model | null => {
         const a_model = retail_models[id]
         if (a_model) {
            return destructModel(a_model, id, lang)
         } else {
            return null
         }
      },
      [destructModel, retail_models]
   )

   const getRetailFamilles = useCallback(
      (lang: string | null = null): Record<string, RetailListItem> => {
         return Object.keys(retail_familles).reduce<Record<string, RetailListItem>>((prev, id) => {
            const a_famille = retail_familles[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_famille, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [retail_familles, getTextInLang]
   )

   const getRetailFamillesAsArray = useCallback(
      (lang: string | null = null): RetailListItem[] => {
         const obj = getRetailFamilles(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getRetailFamilles]
   )

   const getRetailFamillesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): RetailListItem[] => {
         let cmpFunc: (a: RetailListItem, b: RetailListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getRetailFamillesAsArray(lang).sort(cmpFunc)
      },
      [getRetailFamillesAsArray, sortOnIdString, sortOnLabel]
   )

   const getRetailFamilleById = useCallback(
      (id: string, lang: string | null = null): RetailListItem => {
         const a_famille = retail_familles[id] as unknown as Record<string, unknown> | undefined
         if (a_famille) {
            const label = getTextInLang(a_famille, 'label', lang)

            return { ...a_famille, id, label } as RetailListItem
         } else {
            return {} as RetailListItem
         }
      },
      [retail_familles, getTextInLang]
   )

   const getRetailPortes = useCallback(
      (lang: string | null = null): Record<string, RetailListItem> => {
         return Object.keys(portes).reduce<Record<string, RetailListItem>>((prev, id) => {
            const a_porte = portes[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_porte, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [portes, getTextInLang]
   )

   const getRetailPortesAsArray = useCallback(
      (lang: string | null = null): RetailListItem[] => {
         const obj = getRetailPortes(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getRetailPortes]
   )

   const getRetailPortesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): RetailListItem[] => {
         let cmpFunc: (a: RetailListItem, b: RetailListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getRetailPortesAsArray(lang).sort(cmpFunc)
      },
      [getRetailPortesAsArray, sortOnIdString, sortOnLabel]
   )

   const getRetailPorteById = useCallback(
      (id: string, lang: string | null = null): RetailListItem => {
         const a_porte = portes[id] as unknown as Record<string, unknown> | undefined
         if (a_porte) {
            const label = getTextInLang(a_porte, 'label', lang)

            return { ...a_porte, id, label } as RetailListItem
         } else {
            return {} as RetailListItem
         }
      },
      [portes, getTextInLang]
   )

   const getRetailTypologies = useCallback(
      (lang: string | null = null): Record<string, RetailListItem> => {
         return Object.keys(typologies).reduce<Record<string, RetailListItem>>((prev, id) => {
            const a_typo = typologies[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_typo, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [typologies, getTextInLang]
   )

   const getRetailTypologiesAsArray = useCallback(
      (lang: string | null = null): RetailListItem[] => {
         const obj = getRetailTypologies(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getRetailTypologies]
   )

   const getRetailTypologiesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): RetailListItem[] => {
         let cmpFunc: (a: RetailListItem, b: RetailListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getRetailTypologiesAsArray(lang).sort(cmpFunc)
      },
      [getRetailTypologiesAsArray, sortOnIdString, sortOnLabel]
   )

   const getRetailTypologieById = useCallback(
      (id: string, lang: string | null = null): RetailListItem => {
         const a_typo = typologies[id] as unknown as Record<string, unknown> | undefined
         if (a_typo) {
            const label = getTextInLang(a_typo, 'label', lang)

            return { ...a_typo, id, label } as RetailListItem
         } else {
            return {} as RetailListItem
         }
      },
      [typologies, getTextInLang]
   )

   const getRetailCycles = useCallback(
      (lang: string | null = null): Record<string, RetailListItem> => {
         return Object.keys(cycles).reduce<Record<string, RetailListItem>>((prev, id) => {
            const a_cycle = cycles[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_cycle, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [cycles, getTextInLang]
   )

   const getRetailCyclesAsArray = useCallback(
      (lang: string | null = null): RetailListItem[] => {
         const obj = getRetailCycles(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getRetailCycles]
   )

   const getRetailCyclesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): RetailListItem[] => {
         let cmpFunc: (a: RetailListItem, b: RetailListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getRetailCyclesAsArray(lang).sort(cmpFunc)
      },
      [getRetailCyclesAsArray, sortOnIdString, sortOnLabel]
   )

   const getRetailCycleById = useCallback(
      (id: string, lang: string | null = null): RetailListItem => {
         const a_cycle = cycles[id] as unknown as Record<string, unknown> | undefined
         if (a_cycle) {
            const label = getTextInLang(a_cycle, 'label', lang)

            return { ...a_cycle, id, label } as RetailListItem
         } else {
            return {} as RetailListItem
         }
      },
      [cycles, getTextInLang]
   )

   /**
    * Filtre un modèle retail selon le type client effectif et l'exclu boutique.
    * customerType : 'RET' | 'ERE' | 'INT' | null (null = tout visible)
    * hasExcluBoutique : le client a un exclu_boutique non null → voit aussi les excbou
    */
   /**
    * Filtre un modèle retail selon le type client effectif et l'exclu boutique,
    * AVEC en plus la barrière "visibilité famille" issue de la grille
    * DocRetail/GRILLE_ECR_ECF.txt (cf. libs/retail_rules.isVisibleRetailFamille).
    *
    * customerType :
    *   - null → tout visible côté type (admin/N1/N2)
    *   - 'NONE' → rien visible (pas de client sélectionné, rôle aucun)
    *   - 'RET'/'ERE'/'INT' → filtré selon les flags du modèle
    * customer : customer complet (groupe_client + depot_retail + zone_geo) pour
    *   interroger la grille (famille interdite → catalogue masqué). null →
    *   admin/N1/N2 sans client (toutes les familles listées visibles).
    * hasExcluBoutique : le client a un exclu_boutique non null → voit aussi les excbou
    */
   const isRetailModelVisibleForCustomer = useCallback(
      (
         model: Model,
         customerType: 'RET' | 'ERE' | 'INT' | 'NONE' | null,
         hasExcluBoutique: boolean,
         customer: { groupe_client?: string | null, depot_retail?: string | null, zone_geo?: string | null } | null = null,
      ): boolean => {
         if (customerType === 'NONE') return false

         // Barrière famille (grille × customer). Si la famille est interdite
         // pour ce customer (ou hors RETAIL_FAMS = Mobilier/PLV), on filtre.
         const fam = (model as unknown as Record<string, unknown>).fam as string | null | undefined
         if (!isVisibleRetailFamille(fam, customer)) return false

         if (!customerType) return true

         const raw = model as unknown as Record<string, unknown>
         const ret = !!raw.ret
         const ere = !!raw.ere
         const whs = !!raw.whs
         const excbou = !!raw.excbou

         let visible = false
         if (customerType === 'RET') {
            visible = ret
         } else if (customerType === 'ERE') {
            visible = ere
         } else if (customerType === 'INT') {
            visible = ret || ere || whs
         }

         // Retail : un article "exclu boutique" (excbou) n'est visible QUE pour un client
         // ayant un exclu_boutique affecté (hasExcluBoutique), quel que soit son type RET/ERE.
         // Les autres clients ne le voient pas, même si ret/ere matchent.
         if (excbou) {
            visible = hasExcluBoutique
         }

         return visible
      }, []
   )

   return {
      isExcluBoutique,
      isRetailModelVisibleForCustomer,
      retailGammeSort,
      getRetailGammes,
      getRetailGammesAsArray,
      getRetailGammesAsSortedArray,
      getRetailGammeById,
      getRetailGammeForCollectionId,
      getRetailCollections,
      getRetailCollectionsAsArray,
      getRetailCollectionsAsSortedArray,
      getRetailCollectionsInGammes,
      getRetailCollectionsInGammesAsArray,
      getRetailCollectionsInGammesAsSortedArray,
      getAllRetailModels,
      getAllRetailModelsAsArray,
      getAllRetailModelsAsSortedArray,
      getRetailModelsInCollection,
      getRetailModelsInCollectionAsArray,
      getRetailModelsInCollectionAsSortedArray,
      getRetailCollectionById,
      getRetailModelById,
      getRetailFamilles,
      getRetailFamillesAsArray,
      getRetailFamillesAsSortedArray,
      getRetailFamilleById,
      getRetailPortes,
      getRetailPortesAsArray,
      getRetailPortesAsSortedArray,
      getRetailPorteById,
      getRetailTypologies,
      getRetailTypologiesAsArray,
      getRetailTypologiesAsSortedArray,
      getRetailTypologieById,
      getRetailCycles,
      getRetailCyclesAsArray,
      getRetailCyclesAsSortedArray,
      getRetailCycleById
   }
}

export default useRetailCollections
export type { RetailListItem }
